import React, { useContext, useState } from "react";
import { Box, FormControl, TextField, useMediaQuery, useTheme, Collapse } from "@mui/material";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { setAllergies as updateAllergies, setPayMethod as updatePayMethod } from "../redux/userSlice";
import { MinorUserContext } from "../context/UserNameProvider";
import Question from "../components/Question";
import { continueButtonSx, inputLabelSx, textAreaSx } from "../lib/uiformatting";
import ContinueButton from "../components/ContinueButton";
import MoveButton from "../components/MoveButton";
import setPageAndDeferNavigate from "../lib/setPageAndDeferNavigate";

export default function Allergies() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

    const { firstName, isMinor } = useContext(MinorUserContext);
    const user = useSelector((state) => state.user);

    const [allergies, setAllergies] = useState(user.allergies.description || "");
    const [error, setError] = useState(false);
    const [yes, setYes] = useState(user.allergies.has || "");

    const allergyOptions = {
        "Yes": "y",
        "No": "n",
    };

    const handleYesChange = (value) => {
        setYes(value);
        if (value === "n") {
            setAllergies("");
            setError(false);
        }
    };

    const handleAllergiesChange = (e) => {
        setAllergies(e.target.value);
        setError(e.target.value.trim() === "");
    };

    const handleNext = () => {
        dispatch(updateAllergies({ has: yes, description: allergies }));
        if (user.payMethod === "insurance") {
            setPageAndDeferNavigate(dispatch, '/card-file', navigate);
        } else if (user.uberDoc) {
            setPageAndDeferNavigate(dispatch, '/photos', navigate);
        } else {
            dispatch(updatePayMethod("self"));
            setPageAndDeferNavigate(dispatch, '/payment', navigate);
        }
    };

    const handleBack = () => {
        if (user.sex.sexAtBirth === "Male") {
            if (user.treatmentContinuation?.interestedInAccutane === 'Y' && (user.address.state === 'CT' || user.address.state === 'MA')) {
                 setPageAndDeferNavigate(dispatch, '/last-period-date', navigate);
            } else {
                setPageAndDeferNavigate(dispatch, '/sex-at-birth', navigate);
            }
        } else {
            if (user.treatmentContinuation?.interestedInAccutane === 'Y' && (user.address.state === 'CT' || user.address.state === 'MA')) {
                 setPageAndDeferNavigate(dispatch, '/last-period-date', navigate);
            } else {
               setPageAndDeferNavigate(dispatch, '/maternity', navigate);
            }
        }
    };

    return (
        <Question
            onBack={handleBack}
            title={`${isMinor ? `Does ${firstName}` : "Do you"} have any allergies?`}
            subtitle={`Include any allergies to food, dyes, prescriptions, or over the counter medicines (e.g. antibiotics, allergy medications), herbs, vitamins, supplements, or anything else.`}
            input={
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: "350px",
                            gap: "1rem",
                            mt: 2,
                        }}
                    >
                        {Object.keys(allergyOptions).map((label) => (
                            <MoveButton
                                key={allergyOptions[label]}
                                onClick={() => handleYesChange(allergyOptions[label])}
                                title={label}
                                selected={yes === allergyOptions[label]}
                            />
                        ))}
                    </Box>

                    <Collapse in={yes === "y"} mountOnEnter unmountOnExit
                        sx={{
                            width: "100%",
                            maxWidth: isDesktop ? "600px" : "100%",
                            display: "flex",
                            justifyContent: isDesktop ? "center" : "flex-start",
                            mt: "3rem",
                        }}>
                        <FormControl
                            sx={{
                                width: "100%",
                                maxWidth: isDesktop ? "600px" : "100%",
                                display: "flex",
                                justifyContent: isDesktop ? "center" : "flex-start",
                                mt: "0.5rem",
                            }}
                            required={yes === "y"}>
                            <TextField
                                label="What are they?"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textAreaSx,
                                    pl: 1,
                                    pr: 1,
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                autoComplete="off"
                                value={allergies}
                                multiline
                                rows={2}
                                onChange={handleAllergiesChange}
                                error={error}
                                helperText={error ? "Please enter your allergies." : ""}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    },
                                }}
                            />
                        </FormControl>
                    </Collapse>
                </Box>
            }
            footer={
                <Box
                    sx={{ ...continueButtonSx }}
                >
                    <ContinueButton
                        onClick={handleNext}
                        title="Continue"
                        disabled={yes === "" || (yes === "y" && allergies.trim() === "")}
                        selected={yes === "n" || (yes === "y" && allergies.trim() !== "")}
                    />
                </Box>
            }
        />
    );
}
